import React from 'react';
import { Layout } from '../components/Layout';

export function Privacy() {
  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-blue-100">
            How we collect, use, and protect your information
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="lead">
              <strong>Effective Date:</strong> January 1, 2026
            </p>

            <p>
              AOK Financial Inc. ("AOK Financial," "we," "us," or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
            </p>

            <h2>Information We Collect</h2>
            <h3>Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>Name, email address, phone number, and mailing address</li>
              <li>Company name and business information (for B2B partners)</li>
              <li>Account information and credentials</li>
              <li>Financial information necessary for trading and clearing</li>
              <li>Government-issued identification for regulatory compliance</li>
            </ul>

            <h3>Usage Information</h3>
            <p>We automatically collect certain information when you use our services:</p>
            <ul>
              <li>API usage logs and transaction history</li>
              <li>IP addresses and device information</li>
              <li>Browser type and operating system</li>
              <li>Access times and referring URLs</li>
            </ul>

            <h2>How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our trading services</li>
              <li>Process transactions and send transaction confirmations</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Detect, prevent, and address fraud and security issues</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Send important notices and service updates</li>
            </ul>

            <h2>Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Clearing Firms:</strong> RQD and other clearing partners for trade execution and settlement</li>
              <li><strong>Regulators:</strong> SEC, FINRA, and other regulatory bodies as required by law</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in our operations (subject to confidentiality agreements)</li>
              <li><strong>Legal Compliance:</strong> When required by law, court order, or to protect our rights</li>
            </ul>

            <p>
              We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
            </p>

            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul>
              <li>TLS/HTTPS encryption for data transmission</li>
              <li>SHA-256 hashing for API keys and sensitive credentials</li>
              <li>Database encryption at rest</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure logging and monitoring systems</li>
            </ul>

            <p>
              While we take reasonable measures to protect your information, no security system is impenetrable, and we cannot guarantee the absolute security of your data.
            </p>

            <h2>Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to:
            </p>
            <ul>
              <li>Provide our services to you</li>
              <li>Comply with legal and regulatory requirements (typically 6+ years for financial records)</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>

            <h2>Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications</li>
            </ul>

            <p>
              To exercise these rights, please contact us at{' '}
              <a href="mailto:privacy@aokfin.com" className="text-accent-600 hover:text-accent-700">
                privacy@aokfin.com
              </a>.
            </p>

            <h2>Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to:
            </p>
            <ul>
              <li>Maintain your session and authentication state</li>
              <li>Remember your preferences</li>
              <li>Analyze usage patterns and improve our services</li>
            </ul>

            <p>
              You can control cookies through your browser settings, but disabling them may affect your ability to use certain features of our services.
            </p>

            <h2>Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites and encourage you to read their privacy policies.
            </p>

            <h2>Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under 18 years of age, and we do not knowingly collect personal information from children.
            </p>

            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in the United States, where our servers and clearing partners are located. By using our services, you consent to the transfer of your information to the United States.
            </p>

            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website with a new effective date. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>

            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <p>
              <strong>AOK Financial Inc.</strong><br />
              Email: <a href="mailto:privacy@aokfin.com" className="text-accent-600 hover:text-accent-700">privacy@aokfin.com</a><br />
              General Inquiries: <a href="mailto:info@aokfin.com" className="text-accent-600 hover:text-accent-700">info@aokfin.com</a>
            </p>

            <div className="mt-8 text-sm text-gray-600">
              <p>
                <em>Last Updated: January 1, 2026</em>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
